/**
 * Maintenance Table - Activity details table with pagination and CSV export
 */
import { useMemo, useState } from 'react';
import { Paper, Text, Stack, Table, Pagination, Group, Badge, Button } from '@mantine/core';
import { IconDownload } from '@tabler/icons-react';
import type { MaintenanceActivity } from '@/types/maintenance';
import { getTaskTypeLabel } from '@/types/maintenance';

interface MaintenanceTableProps {
  activities: MaintenanceActivity[];
}

const ACTIVITIES_PER_PAGE = 50;

// Simple CSV download utility
function downloadCSV(data: MaintenanceActivity[], filename: string) {
  if (data.length === 0) {
    return;
  }

  // Define headers in desired order
  const headers = [
    'activity_date',
    'task_title',
    'task_type',
    'equipment_name',
    'equipment_model',
    'tank_name',
    'performed',
    'notes',
  ];

  // Map headers to display names
  const headerLabels: Record<string, string> = {
    activity_date: 'Date',
    task_title: 'Task Title',
    task_type: 'Type',
    equipment_name: 'Equipment',
    equipment_model: 'Model',
    tank_name: 'Tank',
    performed: 'Performed',
    notes: 'Notes',
  };

  // Create CSV content
  const csvContent = [
    // Header row
    headers.map((h) => headerLabels[h]).join(','),
    // Data rows
    ...data.map((row) =>
      headers
        .map((h) => {
          let value = row[h as keyof MaintenanceActivity];

          // Special handling for task_type
          if (h === 'task_type') {
            value = getTaskTypeLabel(row.task_type);
          }

          // Special handling for performed
          if (h === 'performed') {
            value = row.performed ? 'Yes' : 'No';
          }

          // Handle null/undefined
          if (value === null || value === undefined) {
            return '';
          }

          // Escape quotes and wrap in quotes if contains comma or quote
          const strValue = String(value);
          if (strValue.includes(',') || strValue.includes('"') || strValue.includes('\n')) {
            return `"${strValue.replace(/"/g, '""')}"`;
          }

          return strValue;
        })
        .join(',')
    ),
  ].join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function MaintenanceTable({ activities }: MaintenanceTableProps) {
  const [page, setPage] = useState(1);

  // Sort activities by date (most recent first)
  const sortedActivities = useMemo(() => {
    return [...activities].sort(
      (a, b) =>
        new Date(b.activity_date).getTime() - new Date(a.activity_date).getTime()
    );
  }, [activities]);

  // Paginate activities
  const paginatedActivities = useMemo(() => {
    const startIndex = (page - 1) * ACTIVITIES_PER_PAGE;
    const endIndex = startIndex + ACTIVITIES_PER_PAGE;
    return sortedActivities.slice(startIndex, endIndex);
  }, [sortedActivities, page]);

  const totalPages = Math.ceil(sortedActivities.length / ACTIVITIES_PER_PAGE);

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Handle CSV export
  const handleExportCSV = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    downloadCSV(sortedActivities, `maintenance-activities-${timestamp}.csv`);
  };

  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <Text size="lg" fw={600}>
            Activity Details
          </Text>

          <Group>
            <Text size="sm" c="dimmed">
              {sortedActivities.length} total activities
            </Text>
            <Button
              leftSection={<IconDownload size={16} />}
              variant="light"
              size="sm"
              onClick={handleExportCSV}
              disabled={sortedActivities.length === 0}
            >
              Export CSV
            </Button>
          </Group>
        </Group>

        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Date</Table.Th>
              <Table.Th>Task Title</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Equipment</Table.Th>
              <Table.Th style={{ textAlign: 'center' }}>Performed</Table.Th>
              <Table.Th>Notes</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {paginatedActivities.length > 0 ? (
              paginatedActivities.map((activity) => {
                return (
                  <Table.Tr key={activity.activity_id}>
                    <Table.Td>
                      <Text size="sm">{formatDate(activity.activity_date)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {activity.task_title}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" size="sm">
                        {getTaskTypeLabel(activity.task_type)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        {activity.equipment_name && (
                          <Text size="sm">{activity.equipment_name}</Text>
                        )}
                        {activity.equipment_model && (
                          <Text size="xs" c="dimmed">
                            {activity.equipment_model}
                          </Text>
                        )}
                      </Stack>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'center' }}>
                      <Badge
                        color={activity.performed ? 'green' : 'orange'}
                        variant="light"
                        size="sm"
                      >
                        {activity.performed ? 'Yes' : 'No'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed" lineClamp={2}>
                        {activity.notes || '-'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                );
              })
            ) : (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text c="dimmed" ta="center" py="md">
                    No maintenance activities found
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>

        {totalPages > 1 && (
          <Group justify="center">
            <Pagination
              value={page}
              onChange={setPage}
              total={totalPages}
              siblings={1}
              boundaries={1}
            />
          </Group>
        )}
      </Stack>
    </Paper>
  );
}
